import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Globe, LogOut, Bell, TrendingUp, TrendingDown, ArrowRightLeft,
  Crown, DollarSign, Banknote, Building2, RefreshCw
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const InternationalDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [forexRates, setForexRates] = useState(null);
  const [settlements, setSettlements] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [forexRes, settlementsRes] = await Promise.all([
        axios.get(`${API}/international/forex-rates`),
        axios.get(`${API}/international/settlements`, { headers, withCredentials: true })
      ]);
      setForexRates(forexRes.data);
      setSettlements(settlementsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Mock data for charts
  const volumeData = [
    { hour: '00:00', volume: 120 },
    { hour: '04:00', volume: 85 },
    { hour: '08:00', volume: 250 },
    { hour: '12:00', volume: 180 },
    { hour: '16:00', volume: 320 },
    { hour: '20:00', volume: 150 }
  ];

  const corridorData = settlements?.corridors?.map(c => ({
    name: c.partner.split('(')[0].trim(),
    volume: c.volume_24h
  })) || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="international-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary rounded-sm flex items-center justify-center">
                <Crown className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="font-serif text-lg font-semibold text-primary hidden sm:block">Royal CBDC</span>
              <span className="text-xs bg-emerald-500/10 text-emerald-600 px-2 py-0.5 rounded font-mono uppercase tracking-wider hidden md:block">
                International Portal
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon">
                <Bell className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <Globe className="h-4 w-4 text-emerald-600" />
                </div>
                <span className="text-sm font-medium hidden sm:block">{user?.name}</span>
              </div>
              <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-serif text-2xl md:text-3xl font-normal text-primary">
            Cross-Border Settlement Hub
          </h1>
          <p className="text-muted-foreground mt-1">International payments, forex, and correspondent banking</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Pending Settlements', value: settlements?.pending_settlements || 0, icon: <RefreshCw className="h-5 w-5 text-yellow-500" /> },
            { label: 'Completed Today', value: settlements?.completed_today || 0, icon: <TrendingUp className="h-5 w-5 text-green-500" /> },
            { label: 'Volume Pending', value: `$${settlements?.total_value_pending || 0}B`, icon: <DollarSign className="h-5 w-5 text-blue-500" /> },
            { label: 'Active Corridors', value: settlements?.corridors?.length || 0, icon: <ArrowRightLeft className="h-5 w-5 text-purple-500" /> }
          ].map((stat, idx) => (
            <Card key={idx} className="dashboard-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">{stat.label}</span>
                  {stat.icon}
                </div>
                <p className="font-mono text-2xl font-semibold" data-testid={`intl-stat-${idx}`}>{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="forex" className="space-y-6">
          <TabsList className="bg-muted/50">
            <TabsTrigger value="forex" data-testid="tab-forex">Foreign Exchange</TabsTrigger>
            <TabsTrigger value="settlements" data-testid="tab-settlements">Settlements</TabsTrigger>
            <TabsTrigger value="corridors" data-testid="tab-corridors">Payment Corridors</TabsTrigger>
          </TabsList>

          {/* Forex Tab */}
          <TabsContent value="forex" className="space-y-6">
            <Card className="sovereign-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-sans text-lg">Exchange Rates</CardTitle>
                    <CardDescription>Real-time rates against CBDC (Base Currency)</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchData} className="gap-2">
                    <RefreshCw className="h-4 w-4" /> Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {forexRates?.rates?.map((rate, idx) => (
                    <div key={idx} className="p-4 rounded-lg bg-muted/30 text-center" data-testid={`forex-rate-${idx}`}>
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Banknote className="h-4 w-4 text-muted-foreground" />
                        <span className="font-mono font-medium">{rate.currency}</span>
                      </div>
                      <p className="font-mono text-2xl font-semibold">{rate.rate}</p>
                      <p className={`text-xs mt-1 ${rate.change_24h >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {rate.change_24h >= 0 ? '+' : ''}{rate.change_24h}%
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="font-sans text-lg">24h Volume Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={volumeData}>
                      <defs>
                        <linearGradient id="colorVolume" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="hour" axisLine={false} tickLine={false} />
                      <YAxis axisLine={false} tickLine={false} />
                      <Tooltip />
                      <Area type="monotone" dataKey="volume" stroke="#10B981" fill="url(#colorVolume)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settlements Tab */}
          <TabsContent value="settlements" className="space-y-6">
            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="font-sans text-lg">Recent Settlements</CardTitle>
                <CardDescription>Cross-border payment settlements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { id: 'SET-001', from: 'Federal Reserve', to: 'Royal CBDC', amount: 125.5, status: 'completed', time: '14:32 UTC' },
                    { id: 'SET-002', from: 'ECB', to: 'Royal CBDC', amount: 89.2, status: 'completed', time: '13:45 UTC' },
                    { id: 'SET-003', from: 'Royal CBDC', to: 'Bank of England', amount: 45.8, status: 'pending', time: '12:15 UTC' },
                    { id: 'SET-004', from: 'Bank of Japan', to: 'Royal CBDC', amount: 67.3, status: 'completed', time: '11:20 UTC' },
                    { id: 'SET-005', from: 'Royal CBDC', to: 'PBOC', amount: 234.1, status: 'processing', time: '10:45 UTC' }
                  ].map((settlement, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 rounded-lg border border-border" data-testid={`settlement-${idx}`}>
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-primary/5 flex items-center justify-center">
                          <ArrowRightLeft className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{settlement.from} → {settlement.to}</p>
                          <p className="text-xs text-muted-foreground">{settlement.id} • {settlement.time}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="font-mono font-semibold">${settlement.amount}M</p>
                        <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                          settlement.status === 'completed' ? 'status-active' :
                          settlement.status === 'pending' ? 'status-pending' :
                          'bg-blue-500/10 text-blue-600 border border-blue-500/20'
                        }`}>
                          {settlement.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Corridors Tab */}
          <TabsContent value="corridors" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="sovereign-card">
                <CardHeader>
                  <CardTitle className="font-sans text-lg">Active Payment Corridors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {settlements?.corridors?.map((corridor, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-muted/30" data-testid={`corridor-${idx}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center">
                            <Building2 className="h-4 w-4 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{corridor.partner}</p>
                            <p className="text-xs text-muted-foreground">${corridor.volume_24h}B (24h)</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                          corridor.status === 'active' ? 'status-active' : 'status-inactive'
                        }`}>
                          {corridor.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="sovereign-card">
                <CardHeader>
                  <CardTitle className="font-sans text-lg">Volume by Corridor</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={corridorData} layout="vertical">
                        <XAxis type="number" axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} width={100} />
                        <Tooltip />
                        <Bar dataKey="volume" fill="#10B981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="sovereign-card">
              <CardHeader>
                <CardTitle className="font-sans text-lg">BIS Standards Compliance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { standard: 'ISO 20022', status: 'compliant' },
                    { standard: 'CPMI-IOSCO', status: 'compliant' },
                    { standard: 'mCBDC Bridge', status: 'active' },
                    { standard: 'FATF Guidelines', status: 'compliant' }
                  ].map((item, idx) => (
                    <div key={idx} className="p-4 rounded-lg bg-muted/30 text-center">
                      <p className="font-mono text-sm font-semibold">{item.standard}</p>
                      <span className="inline-block mt-2 px-2 py-1 rounded text-xs font-medium uppercase status-active">
                        {item.status}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default InternationalDashboard;
